/**
 * Session Initialization Hook
 * SESSION-001: Restores user session on app mount to prevent auth loops.
 * Calls initializeSession exactly once via ref guard, safe with React StrictMode.
 * Must be called at the App root level, before routes are evaluated.
 * @packageDocumentation
 */

import { useRef, useEffect } from 'react';

import { useAuthStore } from '@/stores/auth.js';

/**
 * Custom hook for restoring user session on app mount.
 * Ensures authentication state is hydrated before route protection logic evaluates.
 * Uses ref guard to prevent double-invocation in React StrictMode development mode.
 *
 * This hook should be called once at the App component root, before any Route evaluation.
 * It works in conjunction with ProtectedRoute's checkSession for ongoing validation.
 *
 * @returns { void } This hook returns nothing.
 *
 * @example
 * ```tsx
 * export const App = () => {
 *   useSessionCheck();
 *   return <Routes>...</Routes>;
 * };
 * ```
 */
export const useSessionCheck = (): void => {
  const initializeSession = useAuthStore((state) => state.initializeSession);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      initializeSession();
    }
  }, [initializeSession]);
};
